import React from 'react';

import { Box, Button, Card, Divider, Heading, Paragraph, Text, ZillowLogo } from '@zillow/constellation-10';

import LandlordProvidedDocuments from '../../Widgets/LandlordProvidedDocuments';
import type { Person, Property } from '../../../../shared/models';
import { FULLSTORY_EXCLUDE } from '../../../../shared/constants/analytics';
import { ZILLOW_LEASES_EMAIL } from '../../../constants/stringConstants';
import type { LandlordProvidedDocument } from '../../../models/Types';

type Props = {
  landlordProvidedDocuments: Array<LandlordProvidedDocument>;
  tenant: Person;
  landlord: Person;
  property: Property;
  isSelfUpload?: boolean;
  personalNote: string;
};

export default function OnlineSigningEmail(props: Props) {
  const { landlordProvidedDocuments, property, landlord, tenant, isSelfUpload, personalNote } = props;
  const landlordFirstName = landlord.firstName;
  const landlordFullName = landlord.fullName;
  const tenantFirstName = tenant.firstName;
  const { streetAddress, state, city, zip } = property;

  return (
    <div className={FULLSTORY_EXCLUDE}>
      <Heading
        level={2}
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          marginBottom: 'tight',
        }}
      >
        Preview request email
      </Heading>
      <Text css={{ textStyle: 'body-bold' }}>Subject: </Text>
      <Text css={{ wordBreak: 'break-word' }}>{landlordFirstName} is requesting your signature</Text>
      <Paragraph css={{ marginBottom: 'tight' }} className={FULLSTORY_EXCLUDE}>
        Preview for {streetAddress} {city}, {state} {zip}
      </Paragraph>
      <Card
        tone="neutral"
        elevated={false}
        css={{
          paddingBottom: 'tight',
        }}
      >
        <Box css={{ display: 'flex', justifyContent: 'center', marginTop: 'tight' }}>
          <ZillowLogo style={{ height: '3rem', width: '9rem' }} />
        </Box>
        <Divider css={{ marginBottom: 'loose', marginTop: 'loose' }} />
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'tight',
          }}
        >
          <Paragraph css={{ wordBreak: 'break-word' }}>Hi {tenantFirstName},</Paragraph>

          {isSelfUpload ? (
            <>
              <Paragraph css={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {landlordFullName} is working with us to sign your lease for {streetAddress} {city}, {state} {zip}.
                They’ve asked us to share a copy for your review and signature.
              </Paragraph>

              <Paragraph css={{ wordBreak: 'break-word' }}>
                If you have questions or would like to ask for changes, contact {landlordFirstName} directly.
              </Paragraph>

              <Paragraph css={{ wordBreak: 'break-word' }}>They can revise the lease and resend for signing.</Paragraph>
            </>
          ) : (
            <>
              <Paragraph css={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {landlordFullName} is partnering with us to create your lease for {streetAddress} {city}, {state} {zip}.
                They’ve asked us to share a copy for your review.
              </Paragraph>

              <Paragraph css={{ wordBreak: 'break-word' }}>
                They can revise the lease until everyone approves. Once your lease is finalized, {landlordFirstName}{' '}
                will arrange for it to be signed.
              </Paragraph>

              <Paragraph css={{ wordBreak: 'break-word' }}>
                We also offer explanations for the major parts of the lease. See our Explainer Lease.
              </Paragraph>
            </>
          )}

          <Card
            tone="soft"
            elevated={false}
            outlined={false}
            css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}
          >
            <Heading
              level={3}
              css={{
                wordBreak: 'break-word',
                textStyle:
                  'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
            >
              Message from {landlordFirstName}:
            </Heading>
            <Paragraph>
              <em>”{personalNote}”</em>
            </Paragraph>
          </Card>

          {landlordProvidedDocuments.length > 0 && (
            <>
              <Text>
                Check your Lease Agreement to see which of these additional documents your landlord will also need to
                provide:
              </Text>
              <LandlordProvidedDocuments
                hideExcessDocuments={false}
                showIncludedText={false}
                showAsOrderedList={false}
                landlordProvidedDocuments={landlordProvidedDocuments}
              />
            </>
          )}

          <Button tone="brand" emphasis="filled" fluid disabled>
            View & sign your lease
          </Button>

          <Text>
            Have questions or need help? Find answers on our FAQ page, or contact Customer Support at{' '}
            {ZILLOW_LEASES_EMAIL}.
          </Text>
        </Box>
      </Card>
    </div>
  );
}
